import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface PricingTier {
  name: string;
  price: number;
  description: string;
  addressRange: string;
}

interface SignupFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  selectedPlan: PricingTier | null;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<SignupFormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    selectedPlan: null
  });

  const pricingTiers: PricingTier[] = [
    {
      name: 'STARTER',
      price: 49,
      description: 'Perfect for small property managers',
      addressRange: 'Up to 25 properties'
    },
    {
      name: 'GROWTH',
      price: 99,
      description: 'Ideal for growing property portfolios',
      addressRange: '26-100 properties'
    },
    {
      name: 'PROFESSIONAL',
      price: 179,
      description: 'For larger property portfolios',
      addressRange: '101-500 properties'
    },
    {
      name: 'ENTERPRISE',
      price: 250,
      description: 'For large-scale property management',
      addressRange: 'Unlimited properties'
    }
  ];

  useEffect(() => {
    // If coming from pricing page with selected plan
    if (location.state?.selectedPlan) {
      setFormData(prev => ({
        ...prev,
        selectedPlan: location.state.selectedPlan
      }));
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanSelect = (plan: PricingTier) => {
    setFormData(prev => ({
      ...prev,
      selectedPlan: plan
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.username || !formData.email || !formData.password || !formData.companyName) {
      setError('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (!formData.selectedPlan) {
      setError('Please select a pricing plan');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Clear any existing auth before signup
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Pass signup data to billing page without creating user account yet
      // User will be created only after successful payment confirmation
      navigate('/billing', {
        state: {
          signupData: {
            name: formData.username,
            email: formData.email,
            password: formData.password,
            companyName: formData.companyName,
            role: 'ADMIN'
          },
          selectedPlan: formData.selectedPlan!.name
        }
      });

    } catch (error: any) {
      console.error('Signup error:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Create Your Account
          </h2>
          <p className="mt-2 text-gray-300">
            Start managing your properties with BlockBoard
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sign-up Form */}
          <div className="bg-white/5 rounded-lg p-8 border border-white/10">
            <h3 className="text-xl font-semibold text-white mb-6">Account Information</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-white/80 mb-1">
                  Company Name *
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Your Property Management Company"
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-white/80 mb-1">
                  Username *
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Choose a username"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1">
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1">
                  Password *
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field"
                placeholder="Minimum 8 characters"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-1">
                  Confirm Password *
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Confirm your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !formData.selectedPlan}
                className="w-full mt-6 bg-[#22d3ee] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#06b6d4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating Account...' : 'Create Account & Subscribe'}
              </button>
            </form>

            <p className="mt-4 text-sm text-gray-400 text-center">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-[#22d3ee] hover:text-[#06b6d4] font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>

          {/* Plan Selection */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white">Choose Your Plan</h3>
            
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  formData.selectedPlan?.name === tier.name
                    ? 'bg-[#22d3ee]/20 border-[#22d3ee] ring-2 ring-[#22d3ee]/50'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
                onClick={() => handlePlanSelect(tier)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{tier.name}</h4>
                    <p className="text-sm text-gray-300 mb-2">{tier.description}</p>
                    <p className="text-sm text-gray-400">{tier.addressRange}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">${tier.price}</div>
                    <div className="text-sm text-gray-400">/month</div>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">🎉 14-Day Free Trial</h4>
              <p className="text-sm text-blue-200">
                Payment details are collected at checkout. Your selected plan starts with a
                14-day trial, and billing begins after the trial unless you cancel. A paid
                subscription is required to continue.
              </p>
            </div>

            <div className="mt-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <h4 className="text-sm font-semibold text-green-300 mb-2">✅ What's Included</h4>
              <ul className="text-sm text-green-200 space-y-1">
                <li>• Property groups, units, and resident management</li>
                <li>• Invoices, PDF receipts, and online invoice payments</li>
                <li>• Maintenance requests, documents, announcements, and events</li>
                <li>• Vendor directory, expense tracking, and cash-flow views</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;